import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { CourseForm } from './components/course';
import {
  externalCourseFormSchema,
  type ExternalCourseFormSchema,
} from './components/course/schema';
import { LearningAidForm } from './components/learning-aid';
import {
  externalItemFormSchema,
  type ExternalItemFormSchema,
} from './components/learning-aid/schema';
import { PriceTypes } from '@/models/catalog/course';
import { ResourceTypes } from '@/models/resources';
import { useRefundRequestNavigation } from '@/packages/subsidy/pages/refund-request/steps/resource-description/navigation/use-navigation';
import { RefundRequestWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/resource-description/steps/step-ids';

const stepId = RefundRequestWizardStepIds.ExternalItemForm;

const defaultCourseValues = {
  sale_type: PriceTypes.OneOff,
  disable_duration: false,
};

const defaultLearningAidValues = {
  sale_type: PriceTypes.OneOff,
};

interface FormWrapperProps {
  canGoBack: boolean;
  onBack: () => void;
}

const CourseFormWrapper = ({ canGoBack, onBack }: FormWrapperProps) => {
  const { handleNext, values } = useRefundRequestNavigation({ stepId });

  const storedFormData = values[stepId]?.course;
  const initialValues = storedFormData ?? defaultCourseValues;

  const methods = useForm({
    resolver: zodResolver(externalCourseFormSchema),
    defaultValues: { sale_type: PriceTypes.OneOff },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const onSubmit = (data: ExternalCourseFormSchema) => {
    handleNext({
      [stepId]: { course: data, learningAid: null },
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <CourseForm />
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            {canGoBack && (
              <Button variant="text" onClick={onBack}>
                Voltar
              </Button>
            )}
            <Button type="submit" variant="contained">
              Continuar
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};

interface LearningAidFormWrapperProps extends FormWrapperProps {
  resourceType: ResourceTypes;
}

const LearningAidFormWrapper = ({
  canGoBack,
  onBack,
  resourceType,
}: LearningAidFormWrapperProps) => {
  const { handleNext, values } = useRefundRequestNavigation({ stepId });

  const storedFormData = values[stepId]?.learningAid;
  const initialValues = storedFormData ?? defaultLearningAidValues;

  const methods = useForm<ExternalItemFormSchema>({
    resolver: zodResolver(externalItemFormSchema),
    defaultValues: { sale_type: PriceTypes.OneOff },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const onSubmit = (data: ExternalItemFormSchema) => {
    handleNext({
      [stepId]: { course: null, learningAid: data },
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <LearningAidForm type={resourceType} />
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            {canGoBack && (
              <Button variant="text" onClick={onBack}>
                Voltar
              </Button>
            )}
            <Button type="submit" variant="contained">
              Continuar
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};

export const ExternalItemForm = () => {
  const { handleBack, canGoBack, values } = useRefundRequestNavigation({
    stepId,
  });

  const resourceType = values[RefundRequestWizardStepIds.ExternalResourceType];
  const isCourse = resourceType === ResourceTypes.Course;

  if (isCourse) {
    return <CourseFormWrapper canGoBack={canGoBack} onBack={handleBack} />;
  }

  if (resourceType) {
    return (
      <LearningAidFormWrapper
        canGoBack={canGoBack}
        onBack={handleBack}
        resourceType={resourceType}
      />
    );
  }
};
